import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Eye, ArrowRight, Newspaper, ExternalLink } from "lucide-react";
import type { Article } from "@/types/index";

interface TopArticlesProps {
  articles?: Article[];
  isLoading?: boolean;
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, x: 20 },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 24,
    },
  },
};

function ArticleCard({ article }: { article: Article }) {
  const categoryColors: Record<string, string> = {
    general: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    politics: "bg-red-500/10 text-red-600 dark:text-red-400",
    tech: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    business: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    world: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    sports: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  };

  const categoryColor =
    categoryColors[article.category.toLowerCase()] || categoryColors.general;

  return (
    <motion.div variants={item} className="shrink-0 w-[280px]">
      <Link to={`/content/articles/${article.id}`}>
        <Card className="h-full group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-border overflow-hidden">
          {/* Image */}
          <div className="relative h-36 overflow-hidden bg-muted">
            {article.imageUrl ? (
              <img
                src={article.imageUrl}
                alt={article.headline}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-primary/10">
                <Newspaper className="w-8 h-8 text-muted-foreground/50" />
              </div>
            )}
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            {/* View count badge */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-full">
              <Eye className="w-3 h-3" />
              <span>{article.articleCount.toLocaleString()}</span>
            </div>
            {/* External link on hover */}
            <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="p-2 bg-white/90 dark:bg-black/90 rounded-full">
                <ExternalLink className="w-3.5 h-3.5 text-foreground" />
              </div>
            </div>
          </div>

          <CardContent className="p-4">
            {/* Category */}
            <span
              className={cn(
                "inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-2",
                categoryColor
              )}
            >
              {article.category}
            </span>

            {/* Title */}
            <h3 className="font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors leading-snug">
              {article.headline}
            </h3>

            {/* Date */}
            <p className="text-xs text-muted-foreground mt-2">
              {new Date(article.publishedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  );
}

function ArticleCardSkeleton() {
  return (
    <div className="shrink-0 w-[280px]">
      <Card className="h-full overflow-hidden">
        <Skeleton className="h-36 w-full rounded-none" />
        <CardContent className="p-4">
          <Skeleton className="h-5 w-16 rounded-full mb-2" />
          <Skeleton className="h-5 w-full mb-1" />
          <Skeleton className="h-5 w-2/3 mb-2" />
          <Skeleton className="h-4 w-24 mt-2" />
        </CardContent>
      </Card>
    </div>
  );
}

export function TopArticles({ articles, isLoading = false }: TopArticlesProps) {
  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-6 w-32" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <ArticleCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Top Articles</h2>
        </div>
        <Link
          to="/content"
          className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 group"
        >
          View all
          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      <ScrollArea className="w-full">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="flex gap-4 pb-4"
        >
          {articles && articles.length > 0 ? (
            articles.slice(0, 6).map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))
          ) : (
            <div className="flex items-center justify-center w-full py-12 text-center">
              <div className="flex flex-col items-center">
                <Newspaper className="w-8 h-8 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No articles yet</p>
              </div>
            </div>
          )}
        </motion.div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}

export function TopArticlesSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-6 w-32" />
        </div>
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => (
          <ArticleCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}
